import { ExpressionSpecification } from "mapbox-gl";
import { VelavuDevice, LocationType } from "velavu-js-api";

import iconArgoMobile from "../img/device-symbols/argo-mobile.png";
import iconArgoFixed from "../img/device-symbols/argo-fixed.png";
import iconArgoOffline from "../img/device-symbols/argo-offline.png";
import iconArgoRegistered from "../img/device-symbols/argo-registered.png";
import iconArgoEffectHover from "../img/device-symbols/argo-effect-hover.png";
import iconArgoEffectFocus from "../img/device-symbols/argo-effect-focus.png";
import iconDefaultMobile from "../img/device-symbols/default-mobile.png";
import iconDefaultFixed from "../img/device-symbols/default-fixed.png";
import iconDefaultOffline from "../img/device-symbols/default-offline.png";
import iconDefaultRegistered from "../img/device-symbols/default-registered.png";
import iconDefaultEffectHover from "../img/device-symbols/default-effect-hover.png";
import iconDefaultEffectFocus from "../img/device-symbols/default-effect-focus.png";
import iconBLEMobile from "../img/device-symbols/ble-mobile.png";
import iconBLEFixed from "../img/device-symbols/ble-fixed.png";
import iconBLEOffline from "../img/device-symbols/ble-offline.png";
import iconBLERegistered from "../img/device-symbols/ble-registered.png";
import iconBLEEffectHover from "../img/device-symbols/ble-effect-hover.png";
import iconBLEEffectFocus from "../img/device-symbols/ble-effect-focus.png";
import MapImageReference from "../data/map-image-reference";

/*
 * This file manages image references for device symbols.
 */

/**
 * The prefix used for all device symbol image names in the map.
 * This ensures consistent naming convention for device-related images.
 */
export const deviceSymbolPrefix = "velavu-device-symbol";

/// The device types of a device symbol
export enum DeviceSymbolType {
	Default = "default",
	Argo = "argo",
	BLE = "ble",
}

/// The variants of a device symbol.
export enum DeviceSymbolVariant {
	Mobile = "mobile",
	Fixed = "fixed",
	Offline = "offline",
	Registered = "registered",
	EffectHover = "effect-hover",
	EffectFocus = "effect-focus",
}

/**
 * Defines the visual effects that can be applied to device symbols on the map
 * when interacting with them.
 */
export enum DeviceSymbolEffect {
	Hover = DeviceSymbolVariant.EffectHover,
	Focus = DeviceSymbolVariant.EffectFocus,
}

/// Gets a device image URL by its type and variant
export function mapDeviceSymbolURLForParameters(
	type: DeviceSymbolType,
	variant: DeviceSymbolVariant,
): string {
	return MAP_DEVICE_SYMBOL_IMAGES_MAP.get(
		buildDeviceSymbolName(type, variant),
	)!.src;
}

/// Resolves the device symbol type for a given device
export function resoleDeviceSymbolTypeForDevice(
	device: VelavuDevice,
): DeviceSymbolType {
	if (device.model === "argo") {
		return DeviceSymbolType.Argo;
	} else if (device.model === "ble") {
		return DeviceSymbolType.BLE;
	} else {
		return DeviceSymbolType.Default;
	}
}

/// Resolves the device symbol variant for a given device
export function resolveDeviceSymbolVariantForDevice(
	device: VelavuDevice,
): DeviceSymbolVariant {
	if (device.online === true) {
		if (device.location?.location_type === LocationType.Fixed) {
			return DeviceSymbolVariant.Fixed;
		} else {
			return DeviceSymbolVariant.Mobile;
		}
	} else if (device.online === false) {
		return DeviceSymbolVariant.Offline;
	} else {
		return DeviceSymbolVariant.Registered;
	}
}

/// Gets a device image URL for a device
export function mapDeviceSymbolURLForDevice(device: VelavuDevice): string {
	const type = resoleDeviceSymbolTypeForDevice(device);
	const variant = resolveDeviceSymbolVariantForDevice(device);
	return mapDeviceSymbolURLForParameters(type, variant);
}

/// Builds the name of an image for a device symbol,
/// that can be used to reference the image in the map.
export function buildDeviceSymbolName(
	type: DeviceSymbolType,
	variant: DeviceSymbolVariant,
): string {
	return `${deviceSymbolPrefix}-${type}-${variant}`;
}

/**
 * Builds a Mapbox expression that maps a device to its symbol type
 * @param getHardware An expression that resolves to a hardware model
 * @returns A Mapbox expression that resolves to either the specific hardware type or "default"
 */
export function buildDeviceSymbolTypeExpression(
	getHardware: ExpressionSpecification,
): ExpressionSpecification {
	// prettier-ignore
	return [
		"case", ["any", ["==", getHardware, "argo"], ["==", getHardware, "ble"]],
		getHardware,
		"default",
	];
}

/**
 * Builds a Mapbox expression that resolves to a device symbol image name
 * @param getHardware An expression that resolves to a hardware model
 * @param getOnline An expression that resolves to the `online` property of a device
 * @param getIsFixed An expression that resolves to a boolean that represents whether the device has a fixed location
 */
export function buildDeviceSymbolExpression(
	getHardware: ExpressionSpecification,
	getOnline: ExpressionSpecification,
	getIsFixed: ExpressionSpecification,
): ExpressionSpecification {
	// prettier-ignore
	return [
		"concat",
		deviceSymbolPrefix,
		"-",
		buildDeviceSymbolTypeExpression(getHardware),
		"-",
		//Check online state
		[
			"case",
			["==", getOnline, true], [
				"case",
				getIsFixed, DeviceSymbolVariant.Fixed,
				DeviceSymbolVariant.Mobile,
			],
			["==", getOnline, false], DeviceSymbolVariant.Offline,
			DeviceSymbolVariant.Registered,
		]
	];
}

/**
 * Builds a Mapbox expression that resolves to a device symbol effect image name
 * @param getHardware An expression that resolves to a device hardware
 * @param getEffect An expression that resolves to a {@link DeviceSymbolEffect}
 */
export function buildDeviceEffectExpression(
	getHardware: ExpressionSpecification,
	getEffect: ExpressionSpecification | DeviceSymbolEffect,
): ExpressionSpecification {
	// prettier-ignore
	return [
		"concat",
		deviceSymbolPrefix,
		"-",
		buildDeviceSymbolTypeExpression(getHardware),
		"-",
		getEffect
	];
}

/// All available device symbol references
export const mapDeviceSymbolImages: Readonly<MapImageReference>[] = [
	//Default
	{
		src: iconDefaultMobile,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Default,
			DeviceSymbolVariant.Mobile,
		),
	},
	{
		src: iconDefaultFixed,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Default,
			DeviceSymbolVariant.Fixed,
		),
	},
	{
		src: iconDefaultOffline,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Default,
			DeviceSymbolVariant.Offline,
		),
	},
	{
		src: iconDefaultRegistered,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Default,
			DeviceSymbolVariant.Registered,
		),
	},
	{
		src: iconDefaultEffectHover,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Default,
			DeviceSymbolVariant.EffectHover,
		),
	},
	{
		src: iconDefaultEffectFocus,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Default,
			DeviceSymbolVariant.EffectFocus,
		),
	},

	//GPS
	{
		src: iconArgoMobile,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Argo,
			DeviceSymbolVariant.Mobile,
		),
	},
	{
		src: iconArgoFixed,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Argo,
			DeviceSymbolVariant.Fixed,
		),
	},
	{
		src: iconArgoOffline,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Argo,
			DeviceSymbolVariant.Offline,
		),
	},
	{
		src: iconArgoRegistered,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Argo,
			DeviceSymbolVariant.Registered,
		),
	},
	{
		src: iconArgoEffectHover,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Argo,
			DeviceSymbolVariant.EffectHover,
		),
	},
	{
		src: iconArgoEffectFocus,
		name: buildDeviceSymbolName(
			DeviceSymbolType.Argo,
			DeviceSymbolVariant.EffectFocus,
		),
	},

	//BLE
	{
		src: iconBLEMobile,
		name: buildDeviceSymbolName(
			DeviceSymbolType.BLE,
			DeviceSymbolVariant.Mobile,
		),
	},
	{
		src: iconBLEFixed,
		name: buildDeviceSymbolName(
			DeviceSymbolType.BLE,
			DeviceSymbolVariant.Fixed,
		),
	},
	{
		src: iconBLEOffline,
		name: buildDeviceSymbolName(
			DeviceSymbolType.BLE,
			DeviceSymbolVariant.Offline,
		),
	},
	{
		src: iconBLERegistered,
		name: buildDeviceSymbolName(
			DeviceSymbolType.BLE,
			DeviceSymbolVariant.Registered,
		),
	},
	{
		src: iconBLEEffectHover,
		name: buildDeviceSymbolName(
			DeviceSymbolType.BLE,
			DeviceSymbolVariant.EffectHover,
		),
	},
	{
		src: iconBLEEffectFocus,
		name: buildDeviceSymbolName(
			DeviceSymbolType.BLE,
			DeviceSymbolVariant.EffectFocus,
		),
	},
];

const MAP_DEVICE_SYMBOL_IMAGES_MAP: ReadonlyMap<string, MapImageReference> =
	new Map(mapDeviceSymbolImages.map((img) => [img.name, img]));
